import React, { useCallback, useEffect, useState } from 'react';
import { Button, CompositeView, SimpleView, notification, useRouter } from '@digital/ui';
import { Spin, Table } from 'antd';
import { Link } from 'react-router-dom';
import {
  AtivacaoContratoDetalhe as AtivacaoContratoDetalheModel,
  AtivacaoContratoResultadoDetalhe,
  obterAtivacaoContrato,
} from '@app/api/ativacaoContrato';
import { campoCanonico, formatarMensagemCampos, formatarValorCampo, rotuloCampo } from './apresentacao';
import './styles.css';

const resultadoStatusLabel = (status: string) => {
  if (status === 'Sucesso') return 'Sucesso';
  if (status === 'Divergencia') return 'Divergência';
  if (status === 'Falha') return 'Falha';
  if (status === 'NaoExecutado') return 'Não executado';
  return status || '-';
};

const tipoOperacaoLabel = (tipo: string) => {
  const labels: { [key: string]: string } = {
    Validacao: 'Validação',
    Preenchimento: 'Preenchimento',
  };
  return labels[tipo] || tipo || '-';
};

const ocorrenciaLabel = (tipo: string) => {
  const labels: { [key: string]: string } = {
    Validacao: 'Campo validado conforme as regras do contrato.',
    Preenchimento: 'Campo preenchido ou conferido conforme a minuta.',
  };
  return labels[tipo] || 'Verificar o resultado deste campo.';
};

const mensagemSemConfianca = (mensagem: string | null) => {
  if (!mensagem) return null;
  const limpa = mensagem
    .replace(/Campo extraído da minuta com [0-9]+% de confiança\.?/gi, '')
    .replace(/Extração realizada com [0-9]+% de confiança\.?/gi, '')
    .trim();
  return limpa || null;
};

const resultadoDescricao = (item: AtivacaoContratoResultadoDetalhe) => {
  const mensagem = formatarMensagemCampos(mensagemSemConfianca(item.mensagem));
  const valorAntes = formatarValorCampo(item, item.valorAntes);
  const valorDepois = formatarValorCampo(item, item.valorDepois);
  if (item.tipoOcorrencia === 'Preenchimento' && (item.valorAntes || item.valorDepois)) {
    return `Valor atualizado${valorAntes ? ` de ${valorAntes}` : ''}${valorDepois ? ` para ${valorDepois}` : ''}.`;
  }
  if (item.tipoOcorrencia === 'Validacao' && mensagem && /validado com (?:o valor atual|sucesso)/i.test(mensagem)) {
    return 'Valor conferido com o contrato.';
  }
  return mensagem || ocorrenciaLabel(item.tipoOcorrencia);
};

interface TextoLongoProps {
  value: string | null;
  maxHeight?: number;
}

const TextoLongo = ({ value, maxHeight = 96 }: TextoLongoProps) => {
  const [expandido, setExpandido] = useState(false);
  const texto = value || '-';
  const podeExpandir = texto.length > 240 || texto.split('\n').length > 6;

  return (
    <div>
      <div
        style={{
          maxHeight: podeExpandir && !expandido ? maxHeight : undefined,
          overflowY: podeExpandir && !expandido ? 'hidden' : 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.45,
        }}>
        {texto}
      </div>
      {podeExpandir && (
        <button
          type="button"
          className="ativacao-text-toggle"
          onClick={() => setExpandido(!expandido)}
          aria-expanded={expandido}>
          {expandido ? 'Recolher' : 'Ver texto completo'}
        </button>
      )}
    </div>
  );
};

const valueCell = (item: AtivacaoContratoResultadoDetalhe, value: string | null) => (
  <TextoLongo value={formatarValorCampo(item, value)} />
);

const AtivacaoContratoDetalhe = () => {
  const { history, location, match } = useRouter();
  const id = (match.params as { id: string }).id;
  const [detalhe, setDetalhe] = useState<AtivacaoContratoDetalheModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  const carregarDetalhe = useCallback(() => {
    setLoading(true);
    setErro(false);
    setDetalhe(null);
    obterAtivacaoContrato(id)
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        setDetalhe(data);
      })
      .catch(() => {
        setErro(true);
        notification.error('Não foi possível carregar o processo. Tente novamente.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    carregarDetalhe();
  }, [carregarDetalhe]);

  const resultados = (grupo: 'Minuta' | 'PQ') => {
    if (!detalhe) return [] as AtivacaoContratoResultadoDetalhe[];
    return (detalhe.etapas || []).reduce((lista, etapa) => {
      if (etapa.grupo !== grupo) return lista;
      return lista.concat(etapa.resultados);
    }, [] as AtivacaoContratoResultadoDetalhe[]);
  };

  const prioridadeResultado = (item: AtivacaoContratoResultadoDetalhe) => {
    if (item.status !== 'Sucesso') return 3;
    if (item.tipoOcorrencia === 'Preenchimento') return 2;
    return 1;
  };
  const consolidarMinuta = (items: AtivacaoContratoResultadoDetalhe[]) =>
    items
      .filter(item => item.regraCategoria !== 'Leitura')
      .reduce((lista, item) => {
        const campo = campoCanonico(item.campo);
        if (!campo || campo === 'Anexos') return lista.concat(item);
        const indice = lista.findIndex(resultado => campoCanonico(resultado.campo) === campo);
        if (indice < 0) return lista.concat(item);
        if (prioridadeResultado(item) > prioridadeResultado(lista[indice])) lista[indice] = item;
        return lista;
      }, [] as AtivacaoContratoResultadoDetalhe[]);

  const ordenarPorSituacao = (items: AtivacaoContratoResultadoDetalhe[]) => {
    const ordemStatus = (item: AtivacaoContratoResultadoDetalhe) => {
      if (item.status === 'Sucesso') return 2;
      return 0;
    };

    return items
      .map((item, ordemOriginal) => ({ item, ordemOriginal }))
      .sort((a, b) => ordemStatus(a.item) - ordemStatus(b.item) || a.ordemOriginal - b.ordemOriginal)
      .map(itemOrdenado => itemOrdenado.item);
  };

  const minuta = ordenarPorSituacao(consolidarMinuta(resultados('Minuta')));
  const pq = ordenarPorSituacao(resultados('PQ'));
  const pendencias = (items: AtivacaoContratoResultadoDetalhe[]) =>
    items.filter(item => item.status !== 'Sucesso').length;
  const abaQuery = new URLSearchParams(location.search).get('aba');
  const abaPadrao = pendencias(minuta) > 0 || pq.length === 0 ? 'minuta' : 'pq';
  const abaAtiva = abaQuery === 'minuta' || abaQuery === 'pq' ? abaQuery : abaPadrao;
  const estadoRota = location.state as { retorno?: string } | undefined;
  const rotaRetorno = estadoRota && estadoRota.retorno ? estadoRota.retorno : '/ativacaoContratos';

  const selecionarAba = (aba: string) => {
    const parametros = new URLSearchParams(location.search);
    parametros.set('aba', aba);
    history.replace({ pathname: location.pathname, search: '?' + parametros.toString(), state: location.state });
  };

  const columns = [
    {
      title: 'Campo / item',
      key: 'campo',
      width: 240,
      render: (_: any, item: AtivacaoContratoResultadoDetalhe) => <strong>{rotuloCampo(item)}</strong>,
    },
    {
      title: 'Extraído da minuta',
      key: 'valorMinuta',
      width: 280,
      render: (_: any, item: AtivacaoContratoResultadoDetalhe) => valueCell(item, item.valorMinuta),
    },
    {
      title: 'No contrato',
      key: 'valorSgc',
      width: 280,
      render: (_: any, item: AtivacaoContratoResultadoDetalhe) => valueCell(item, item.valorSgc || item.valorAntes),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_: any, item: AtivacaoContratoResultadoDetalhe) => <span>{resultadoStatusLabel(item.status)}</span>,
    },
    {
      title: 'Tipo',
      key: 'tipoOcorrencia',
      width: 170,
      className: 'ativacao-align-left',
      render: (_: any, item: AtivacaoContratoResultadoDetalhe) => <span>{tipoOperacaoLabel(item.tipoOcorrencia)}</span>,
    },
    {
      title: 'Resultado',
      key: 'resultado',
      width: 360,
      className: 'ativacao-align-left',
      render: (_: any, item: AtivacaoContratoResultadoDetalhe) => (
        <TextoLongo value={resultadoDescricao(item)} maxHeight={72} />
      ),
    },
  ];

  return (
    <CompositeView>
      <SimpleView>
        <div className="ativacao-page ativacao-detail-page" aria-live="polite" aria-busy={loading}>
          <Spin spinning={loading} tip="Carregando processo…">
            {erro && !loading && (
              <div className="ativacao-error-state" role="alert">
                <span className="ativacao-eyebrow">Falha no carregamento</span>
                <h1>Não foi possível abrir este processo</h1>
                <p>Verifique o identificador ou tente carregar os dados novamente.</p>
                <div className="ativacao-actions">
                  <Button type="primary" onClick={carregarDetalhe}>
                    Tentar novamente
                  </Button>
                  <Link to={rotaRetorno}>Voltar para ativações</Link>
                </div>
              </div>
            )}
            {detalhe && (
              <div>
                <header className="ativacao-detail-header" aria-label="Detalhes da ativação">
                  <Link className="ativacao-back-link" to={rotaRetorno}>
                    <span aria-hidden="true">←</span> Voltar para ativações
                  </Link>
                  <div className="ativacao-detail-header__main">
                    <dl className="ativacao-detail-meta">
                      <div className="ativacao-detail-meta__item">
                        <dt>Ordem de compra:</dt>
                        <dd>{detalhe.ordemCompraNumero == null ? 'Não informada' : detalhe.ordemCompraNumero}</dd>
                      </div>
                      <div className="ativacao-detail-meta__item">
                        <dt>Contrato:</dt>
                        <dd>{detalhe.contratoNumero == null ? 'Sem número' : detalhe.contratoNumero}</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="ativacao-detail-summary">{detalhe.resumo || 'Sem resumo disponível.'}</div>
                </header>
                <section className="ativacao-document-panel" aria-label="Documentos do processo">
                  <div className="ativacao-document-selector" role="tablist" aria-label="Selecionar documento">
                    <button
                      id="ativacao-minuta-tab"
                      type="button"
                      role="tab"
                      aria-selected={abaAtiva === 'minuta'}
                      aria-controls="ativacao-document-content"
                      className={`ativacao-document-option${abaAtiva === 'minuta' ? ' is-active' : ''}`}
                      onClick={() => selecionarAba('minuta')}>
                      <strong>Minuta</strong>
                      <span>{detalhe.minutaNome || 'Minuta não identificada'}</span>
                    </button>
                    <button
                      id="ativacao-pq-tab"
                      type="button"
                      role="tab"
                      aria-selected={abaAtiva === 'pq'}
                      aria-controls="ativacao-document-content"
                      className={`ativacao-document-option${abaAtiva === 'pq' ? ' is-active' : ''}`}
                      onClick={() => selecionarAba('pq')}>
                      <strong>PQ</strong>
                      <span>{detalhe.pqNome || 'Planilha não identificada'}</span>
                    </button>
                  </div>
                  <div
                    id="ativacao-document-content"
                    className="ativacao-document-content"
                    role="tabpanel"
                    aria-labelledby={abaAtiva === 'minuta' ? 'ativacao-minuta-tab' : 'ativacao-pq-tab'}>
                    <Table
                      className="no-bold-table ativacao-table"
                      dataSource={abaAtiva === 'minuta' ? minuta : pq}
                      rowKey="id"
                      columns={columns}
                      size="small"
                      pagination={false}
                      scroll={{ x: 1470 }}
                      locale={{
                        emptyText:
                          abaAtiva === 'minuta'
                            ? 'Nenhum resultado da minuta foi registrado.'
                            : 'Nenhum resultado da PQ foi registrado.',
                      }}
                    />
                  </div>
                </section>
              </div>
            )}
          </Spin>
        </div>
      </SimpleView>
    </CompositeView>
  );
};

export default AtivacaoContratoDetalhe;
